// NOTE: this is called "utils.ts" instead of "util.ts" because naming it "util" made the module
// link incorrectly. i'm not sure what was causing this... -ss
import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import * as api from 'dragonmouse-shared/api/forge';
import {
	Attachment,
	DesignSystem,
	Device,
	DomElement,
	Frame,
	Scene,
} from 'dragonmouse-shared/interfaces';
import { setLoading } from 'redux/mode/actions';
import { DeviceConfig } from 'redux/mode/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import { tabbable } from 'tabbable';
import { unthemed } from 'tokens';
import { v4 as uuid } from 'uuid';
import { getActiveVisualEditsHash } from 'visualEdits';
import { VITE_FORGE_URL } from './constants';

export type ExportFormat = 'png' | 'svg' | 'html';

export const isFeatureEnabled = (feature: string): boolean => {
	const params = new URLSearchParams(window.location.search);
	return !!params.get(feature);
};

export const validPathname = (input: string) => {
	const validRegex = /[^a-zA-Z0-9\\/\-_ #:@?&=%+!$().*;`\\[\]"'<>^{}]/;
	return !validRegex.test(input);
};

export const validUrl = (input: string) => {
	const validRegex =
		/^(http|https|ftp):\/\/|(^localhost:([0-9]{1,5}))|(^www\.)?([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}(:[0-9]{1,5})?([\\/?#].*)?$/;
	return validRegex.test(input);
};

export const validEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const tabToNextElement = () => {
	const elements = tabbable(document.body);
	const activeElement = document.activeElement as HTMLElement | SVGElement | null;
	if (activeElement === null) return;
	const index = elements.indexOf(activeElement);
	if (index === elements.length - 1) {
		elements[0].focus();
	} else {
		elements[index + 1].focus();
	}
};

export const scrollIntoViewIfNeeded = (ref: React.RefObject<HTMLElement>) => {
	if (!ref.current) return;

	// Turns out that sometimes this is called BEFORE the ref's element is ready
	// We have a whole list of these
	// and I think React defers loading them while it's warming up.
	// This led to an odd bug where the whole page scrolled because the ref couldn't find its container
	// So that's why this seems overly complex!

	const isElementReady = () => {
		return ref.current && ref.current.getBoundingClientRect().height > 0;
	};

	// Try to scroll, but only if element is ready
	const tryScroll = () => {
		if (!ref.current) return;
		if (!isElementReady()) {
			setTimeout(tryScroll, 50);
			return;
		}
		try {
			// Make sure it has scrollable parent element by checking computed styles
			// This works with styled-components and other CSS-in-JS solutions
			let element = ref.current;
			let scrollParent = null;
			while (element && element !== document.body) {
				const computedStyle = window.getComputedStyle(element);
				if (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll') {
					scrollParent = element;
					break;
				}
				element = element.parentElement as HTMLElement;
			}
			if (!scrollParent) return;

			ref.current.scrollIntoView({
				behavior: 'instant',
				block: 'nearest',
			});
		} catch (e) {
			console.error('Scroll attempt failed:', e);
		}
	};

	tryScroll();
};

export const initialChatDevice = (devices: Device[]): Device | null => {
	const tabletDevice = devices.find((d) => d.type === 'mobile');
	if (tabletDevice) return tabletDevice;
	return devices[0];
};

export const logout = async () => {
	if (typeof window.mixpanel !== 'undefined') {
		window.mixpanel.reset();
	}

	store.dispatch(setLoading(true));

	// wait for 500ms just in case redux has some odd delay
	await new Promise((resolve) => setTimeout(resolve, 500));
	window.location.href = VITE_FORGE_URL + '/auth/logout';
};

export const downloadBlob = (blob: Blob, filename: string) => {
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.style.display = 'none';
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(a);
};

export const slugify = (s: string): string =>
	s
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

// Build a filesystem-friendly filename from a scene title and a qualifier token.
// e.g. buildExportFilename('Portfolio Site', 'React + Tailwind', 'project') -> 'portfolio-site-react-tailwind.zip'
// Falls back to `fallback` when the scene title slugifies to an empty string.
export const buildExportFilename = (
	sceneTitle: string | undefined | null,
	token: string,
	fallback: string,
	extension: string = 'zip'
): string => {
	const sceneSlug = sceneTitle ? slugify(sceneTitle) : '';
	const base = sceneSlug || fallback;
	const tokenSlug = slugify(token);
	return tokenSlug ? `${base}-${tokenSlug}.${extension}` : `${base}.${extension}`;
};

//------------------------------------------------------------------------------------------------
// PLATFORM DETECTION (Mac/Windows/Linux/etc)
//------------------------------------------------------------------------------------------------

// taken from
// https://medium.com/@julienetienne/detecting-the-os-browser-and-browser-version-when-old-methods-have-depreciated-91c08a1e6407
// and
// https://github.com/julienetie/detect-browser/blob/main/detect-browser.js

const navigatorErrorMessage =
	'Could not find `userAgent` or `userAgentData` window.navigator properties to set `os`, `browser` and `version`';
const removeExcessMozillaAndVersion = /^mozilla\/\d\.\d\W/;
const browserPattern = /(\w+)\/(\d+\.\d+(?:\.\d+)?(?:\.\d+)?)/g;
const engineAndVersionPattern = /^(ver|cri|gec)/;
const brandList = ['chrome', 'opera', 'safari', 'edge', 'firefox'];
const unknown = 'Unknown';
// @ts-expect-error window.navigator.userAgentData is not typed
let userAgentData = window.navigator.userAgentData;
let userAgent = window.navigator.userAgent;

const mobiles = {
	iphone: /iphone/,
	ipad: /ipad|macintosh/,
	android: /android/,
};

const desktops = {
	windows: /win/,
	mac: /macintosh/,
	linux: /linux/,
};

export const detectPlatform = (
	customUserAgent: any = undefined,
	customUserAgentData: any = undefined
) => {
	// Use a provided UA string instead of the browser's UA
	userAgent = typeof customUserAgent === 'string' ? customUserAgent : userAgent;

	// Use a provided UA data string instead of the browser's UA data
	userAgentData = typeof customUserAgentData === 'string' ? customUserAgentData : userAgentData;

	if (userAgent) {
		const ua = userAgent.toLowerCase().replace(removeExcessMozillaAndVersion, '');

		// Determine the operating system.
		const mobileOS = Object.keys(mobiles).find(
			// @ts-expect-error type issue with mobiles[os]
			(os) => mobiles[os].test(ua) && window.navigator.maxTouchPoints >= 1
		);
		// @ts-expect-error type issue with desktops[os]
		const desktopOS = Object.keys(desktops).find((os) => desktops[os].test(ua));
		const os = mobileOS || desktopOS;

		// Extract browser and version information.
		const browserTest = ua.match(browserPattern);
		const versionRegex = /version\/(\d+(\.\d+)*)/;
		const safariVersion = ua.match(versionRegex);
		const saVesion = Array.isArray(safariVersion) ? safariVersion[1] : null;
		const browserOffset =
			browserTest &&
			(browserTest.length > 2 && !engineAndVersionPattern.test(browserTest[1]) ? 1 : 0);
		const browserResult =
			browserTest && browserTest[browserTest.length - 1 - (browserOffset || 0)].split('/');
		const browser = browserResult && browserResult[0];
		const version = saVesion ? saVesion : browserResult && browserResult[1];

		return { os, browser, version };
	} else if (userAgentData) {
		const os = userAgentData.platform.toLowerCase();
		let platformData;

		// Extract platform brand and version information.
		for (const agentBrand of userAgentData.brands) {
			const agentBrandEntry = agentBrand.brand.toLowerCase();
			const foundBrand = brandList.find((brand) => {
				//eslint-disable-line
				if (agentBrandEntry.includes(brand)) {
					return brand;
				}
			});
			if (foundBrand) {
				platformData = { browser: foundBrand, version: agentBrand.version };
				break;
			}
		}
		const brandVersionData = platformData || { browser: unknown, version: unknown };
		return { os, ...brandVersionData };
	} else {
		// Log error message if there's a problem.
		console.error(navigatorErrorMessage);

		return {
			// Ignore the VSCode strikethough. Disable linting line if necessary. This is just a fallback
			os: navigator.platform || unknown,
			browser: unknown,
			version: unknown,
		};
	}
};
const platform = detectPlatform();

// the event has the primary modifier key (ctrl on windows, cmd on mac)
export const hasPrimaryModifier = (
	event:
		| React.KeyboardEvent<HTMLElement>
		| React.MouseEvent<HTMLElement>
		| KeyboardEvent
		| React.MouseEvent
		| MouseEvent
) => {
	return isMac() ? event.metaKey : event.ctrlKey;
};

// the event has the secondary modifier key (alt on windows, option on mac)
export const hasSecondaryModifier = (
	event:
		| React.KeyboardEvent<HTMLElement>
		| React.MouseEvent<HTMLElement>
		| KeyboardEvent
		| React.MouseEvent
		| MouseEvent
) => {
	return isMac() ? event.altKey : event.altKey;
};

//------------------------------------------------------------------------------------------------
// DEVICE DETECTION (Mobile/Desktop/etc)
//------------------------------------------------------------------------------------------------

export const detectDeviceConfig = (): DeviceConfig => {
	// We use user agent and touch capability detection to determine the device config
	// we do NOT use screen size since it's pretty dodgy (landscape phones can get quite wide, etc)
	// User agent should be quite reliable.
	// Decided to add logging to see if unexpected corner cases arise (instead of trying to head them off with actual code)

	const ua = navigator.userAgent;

	// Validate user agent - but if there are errors we keep going (this function always returns a reasonable fallback)
	if (!ua || ua.length === 0) {
		Sentry.captureMessage('Device detection: Missing user agent', 'error');
	} else if (ua.length < 10) {
		Sentry.captureMessage(`Device detection: Suspiciously short user agent: "${ua}"`, 'warning');
	} else if (ua.length > 1000) {
		Sentry.captureMessage(
			`Device detection: Suspiciously long user agent (${ua.length} chars)`,
			'warning'
		);
	}

	// get basic capabilities
	const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	const isPhone =
		/iPhone|iPod/i.test(ua) ||
		(/Android/i.test(ua) && /Mobile/i.test(ua)) ||
		/webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);

	// Mode: phones get mobile, everything else gets desktop
	const kind = isPhone ? 'mobile' : 'desktop';

	// Input mode: any touch-capable device reports touch
	const input = hasTouch ? 'touch' : 'mouse';

	// Log any unexpected corner cases
	const screenWidth = window.screen.width;
	const screenHeight = window.screen.height;
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;
	// Phone without touch capability (very suspicious)
	if (isPhone && !hasTouch) {
		Sentry.captureMessage(
			`Device detection: Phone detected without touch capability - UA: "${ua}", maxTouchPoints: ${navigator.maxTouchPoints}`,
			'warning'
		);
	}
	// Desktop with touch but not a known touch device (could be legitimate but worth noting)
	if (!isPhone && hasTouch && !/iPad|Android/i.test(ua)) {
		Sentry.captureMessage(
			`Device detection: Desktop with touch capability - UA: "${ua}", maxTouchPoints: ${navigator.maxTouchPoints}`,
			'info'
		);
	}
	// Screen size anomalies
	if (kind === 'desktop' && (screenWidth < 800 || screenHeight < 600)) {
		Sentry.captureMessage(
			`Device detection: Very small desktop screen - ${screenWidth}x${screenHeight}, UA: "${ua}"`,
			'warning'
		);
	}
	if (kind === 'mobile' && (screenWidth > 2000 || screenHeight > 2000)) {
		Sentry.captureMessage(
			`Device detection: Very large mobile screen - ${screenWidth}x${screenHeight}, UA: "${ua}"`,
			'warning'
		);
	}
	// Viewport vs screen size discrepancies
	const viewportToScreenRatio = viewportWidth / screenWidth;
	if (viewportToScreenRatio < 0.3 || viewportToScreenRatio > 1.5) {
		Sentry.captureMessage(
			`Device detection: Unusual viewport/screen ratio - viewport: ${viewportWidth}x${viewportHeight}, screen: ${screenWidth}x${screenHeight}, ratio: ${viewportToScreenRatio.toFixed(
				2
			)}`,
			'info'
		);
	}

	// Log the final configuration so we can see device type for any session we're debugging in Sentry
	Sentry.addBreadcrumb({
		message: 'Device configuration detected',
		category: 'device',
		level: 'info',
		data: {
			kind,
			input,
			userAgent: ua,
			screenSize: `${screenWidth}x${screenHeight}`,
			viewportSize: `${viewportWidth}x${viewportHeight}`,
			maxTouchPoints: navigator.maxTouchPoints,
			hasTouchStart: 'ontouchstart' in window,
		},
	});

	const config: DeviceConfig = { kind, input };
	return config;
};

//------------------------------------------------------------------------------------------------
// SHORTCUT DISPLAY HELPERS
//------------------------------------------------------------------------------------------------

type ModifierKeys = {
	ctrl?: boolean; // cmd on Mac
	shift?: boolean;
	alt?: boolean; // option on Mac
	// note we don't use Mac's native "ctrl" for any shortcuts
};

export const isMac = () => {
	return platform.os === 'mac';
};

export const generateShortcutText = (key: string, modifiers: ModifierKeys): string => {
	const mac = isMac();

	const macSymbols: { [key: string]: string } = {
		ctrl: '⌘',
		shift: '⇧',
		alt: '⌥',
	};

	const windowsSymbols: { [key: string]: string } = {
		ctrl: 'Ctrl',
		shift: 'Shift',
		alt: 'Alt',
	};

	const symbols = mac ? macSymbols : windowsSymbols;

	let shortcut = '';

	if (modifiers.ctrl) {
		shortcut += symbols.ctrl + (mac ? '' : '+');
	}
	if (modifiers.shift) {
		shortcut += symbols.shift + (mac ? '' : '+');
	}
	if (modifiers.alt) {
		shortcut += symbols.alt + (mac ? '' : '+');
	}

	// use mac-specific glyphs when appropriate
	if (key === 'Del' && mac) {
		key = '⌫';
	}

	// make sure the first letter of key is capitalized
	shortcut += key.charAt(0).toUpperCase() + key.slice(1);

	return shortcut;
};

export const getElementName = (element: DomElement) => {
	// If it only has a className, return it in title case
	if (!element.tagName || element.tagName === '') {
		if (element.className) {
			return element.className
				.split('-')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');
		} else {
			console.error('Element has no className', element);
			return 'Element';
		}
	}

	// Convert tag names to designer-friendly names
	const tagNameMap: Record<string, string> = {
		h1: 'Header 1',
		h2: 'Header 2',
		h3: 'Header 3',
		h4: 'Header 4',
		h5: 'Header 5',
		h6: 'Header 6',
		p: 'Paragraph',
		div: 'Container',
		span: 'Text',
		a: 'Link',
		button: 'Button',
		input: 'Input',
		i: 'Icon',
		img: 'Image',
		ul: 'List',
		ol: 'Numbered List',
		li: 'List Item',
		nav: 'Navigation',
		header: 'Header',
		footer: 'Footer',
		main: 'Main Content',
		section: 'Section',
		article: 'Article',
		aside: 'Sidebar',
		form: 'Form',
		label: 'Label',
		textarea: 'Text Area',
		select: 'Dropdown',
		option: 'Option',
		table: 'Table',
		tr: 'Table Row',
		td: 'Table Cell',
		th: 'Table Header',
		video: 'Video',
		audio: 'Audio',
		canvas: 'Canvas',
		svg: 'SVG',
		path: 'Path',
		rect: 'Rectangle',
		circle: 'Circle',
		ellipse: 'Ellipse',
		line: 'Line',
		polygon: 'Polygon',
		polyline: 'Polyline',
		text: 'Text',
		g: 'Group',
		defs: 'Definitions',
		clipPath: 'Clip Path',
		mask: 'Mask',
		filter: 'Filter',
		feGaussianBlur: 'Blur',
		feColorMatrix: 'Color Matrix',
		feDropShadow: 'Drop Shadow',
		linearGradient: 'Linear Gradient',
		radialGradient: 'Radial Gradient',
		stop: 'Gradient Stop',
		pattern: 'Pattern',
		animate: 'Animation',
		animateTransform: 'Transform Animation',
		animateMotion: 'Motion Animation',
		set: 'Set Animation',
		use: 'Use Element',
		symbol: 'Symbol',
		metadata: 'Metadata',
		title: 'Title',
		desc: 'Description',
	};

	// DS custom elements: ds-button → "Button", ds-card-header → "Card Header"
	const lowerTag = element.tagName.toLowerCase();
	if (lowerTag.startsWith('ds-')) {
		return lowerTag
			.slice(3)
			.split('-')
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(' ');
	}

	const friendlyTagName = tagNameMap[lowerTag] || element.tagName;

	// Pick the most descriptive class — first non-utility class, ignoring BEM modifiers
	if (element.className && element.className !== '') {
		const classes = element.className.split(/\s+/).filter((c) => c);
		// Find a "base" class (not a modifier like --primary or a utility like ds-button--lg)
		const baseClass = classes.find((c) => !c.includes('--')) ?? classes[0];
		if (baseClass) {
			let friendlyClassName = baseClass
				.split('-')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');

			// Clean up Font Awesome class names
			if (/^(Fas|Far|Fab|Fal|Fad|Fat)\s+Fa\s+/i.test(friendlyClassName)) {
				friendlyClassName = friendlyClassName.replace(/^(Fas|Far|Fab|Fal|Fad|Fat)\s+fa\s+/i, '');
			}

			return `${friendlyTagName} (${friendlyClassName})`;
		}
	}

	return friendlyTagName;
};
//------------------------------------------------------------------------------------------------
// COLOR HELPERS
//------------------------------------------------------------------------------------------------

// take a hex string and convert it to triple of rgb values with values from 0 to 1.0
export const hexToRgb = (hex: string) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16) / 255,
				g: parseInt(result[2], 16) / 255,
				b: parseInt(result[3], 16) / 255,
		  }
		: null;
};

// take floating point values from 0 to 1.0 and convert them to a hex string
export const rgbToHex = (rgb: { r: number; g: number; b: number }) => {
	const clampedR = Math.max(0, Math.min(255, Math.round(rgb.r * 255)));
	const clampedG = Math.max(0, Math.min(255, Math.round(rgb.g * 255)));
	const clampedB = Math.max(0, Math.min(255, Math.round(rgb.b * 255)));

	return '#' + ((1 << 24) + (clampedR << 16) + (clampedG << 8) + clampedB).toString(16).slice(1);
};

// Convert a CSS color string (rgb/rgba as returned by getComputedStyle) to
// "#RRGGBB" hex. Drops alpha. Returns "#000000" if parse fails.
export const cssColorToHex = (cssColor: string): string => {
	const m = cssColor.match(/\d+/g);
	if (!m || m.length < 3) return '#000000';
	const [r, g, b] = m.slice(0, 3).map(Number);
	return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
};

// compute a color for text, lines, icons, etc so that they will
// stand out from the background with good contrast
//
// backgroundColor is a hex string
export const contrastingForegroundColor = (backgroundColor: string) => {
	const rgb = hexToRgb(backgroundColor);
	if (!rgb) return unthemed.gray['600'];
	const grayscale = rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114;

	if (grayscale > 0.6) {
		// if the background is in the top 30% of brightness ranges, use dark text,
		// that gets brighter as background gets brighter
		const value = 0.5 + 0.5 * (grayscale - 0.6);
		return rgbToHex({ r: value, g: value, b: value }); // dark text
	} else {
		// use light text that gets darker as background gets darker
		// (so always just a bit lighter than the background)
		const value = 0.4 + grayscale * 0.4;
		return rgbToHex({ r: value, g: value, b: value }); // light text
	}
};

// Gets 2-letter initials: first+last initials for "First Last", or first 2 chars for single word.
export const getInitials = (name: string): string => {
	const parts = (name || '').trim().split(/\s+/);
	if (parts.length >= 2) {
		return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase() || 'U';
	}
	const s = (name || '').trim();
	return s.slice(0, 2).toUpperCase() || 'U';
};

// Generate a consistent color based on the user's name
export const getColorFromName = (name: string) => {
	const colors = [
		'#FF6B6B', // Red
		'#4ECDC4', // Teal
		'#45B7D1', // Blue
		'#96CEB4', // Green
		'#FFEAA7', // Yellow
		'#DDA0DD', // Plum
		'#98D8C8', // Mint
		'#F7DC6F', // Gold
		'#BB8FCE', // Purple
		'#85C1E9', // Sky Blue
	];
	if (name.length === 0) {
		return colors[0];
	}
	const index = name.charCodeAt(0) % colors.length;
	return colors[index];
};

// Avatar theme type - design system color families used for gradients.
type AvatarTheme = {
	blue: { [key: number]: string };
	purple: { [key: number]: string };
	green: { [key: number]: string };
	red: { [key: number]: string };
	orange: { [key: number]: string };
};

// Returns gradient for avatar backgrounds. Uses design system colors for variety and white-text contrast.
export const getAvatarGradient = (name: string, theme: AvatarTheme): string => {
	const gradients: Array<[keyof AvatarTheme, number, keyof AvatarTheme, number]> = [
		['blue', 600, 'purple', 700],
		['blue', 700, 'blue', 800],
		['blue', 400, 'green', 400],
		['purple', 500, 'purple', 700],
		['blue', 500, 'purple', 500],
		['orange', 700, 'red', 700],
		['blue', 600, 'blue', 800],
		['blue', 800, 'purple', 700],
	];
	const index = name.length ? name.charCodeAt(0) % gradients.length : 0;
	const [c1, s1, c2, s2] = gradients[index];
	const from = theme[c1]?.[s1] ?? theme.blue[600];
	const to = theme[c2]?.[s2] ?? theme.purple[700];
	return `linear-gradient(135deg, ${from}, ${to})`;
};
//------------------------------------------------------------------------------------------------
// URL HELPERS
//------------------------------------------------------------------------------------------------

// Builds a frame image URL using only the frameId. The backend looks up siteId, veHash, etc.
// Less precise caching than frameImageUrl (no veHash in the URL for browser cache-busting)
// but works when the Frame object isn't available on the client.
export const frameImageUrlById = (frameId: string): string => {
	return `${VITE_FORGE_URL}/frame/${frameId}/image`;
};

export const frameImageUrl = (frame: Frame, proxy: boolean = false): string | null => {
	if (frame.siteId) {
		const url = new URL(VITE_FORGE_URL + `/site/${frame.siteId}/image`);
		// If the frame has a site id, we load the image via the site.
		url.searchParams.append('width', frame.width.toString());
		url.searchParams.append('height', frame.height.toString());
		url.searchParams.append('frameId', frame.id);

		const veHash = getActiveVisualEditsHash(frame);
		if (veHash) {
			url.searchParams.append('veHash', veHash);
		}
		if (frame.metadata?.mode) {
			url.searchParams.append('mode', frame.metadata.mode);
		}

		if (proxy) url.searchParams.append('proxy', 'true');
		return url.toString();
	} else if (frame.imagePath) {
		const url = new URL(VITE_FORGE_URL + `/frame/${frame.id}/image`);
		// Otherwise load via the frame itself
		// NOTE this method will only allow the currently saved site or image to be
		// loaded. This won't work with e.g. undo plus optimistic updates where
		// before the server has updated the frame to a new site we try to load the
		// image. That's why the first method exists.
		if (proxy) {
			url.searchParams.append('proxy', 'true');
		}

		// For cache-busting
		if (frame.imagePath) url.searchParams.append('imagePath', frame.imagePath);
		return url.toString();
	}

	return null;
};

export const frameSiteUrl = (frame: Frame, params: Record<string, any> = {}): string | null => {
	if (!frame.siteId) return null;
	const url = new URL(VITE_FORGE_URL + `/site/${frame.siteId}`);
	url.searchParams.append('frameId', frame.id); // frame Id is needed for v1 sites. It does not appear that v2 sites use this param
	if (frame.metadata?.mode) {
		url.searchParams.append('mode', frame.metadata.mode);
	}
	for (const [key, value] of Object.entries(params)) {
		url.searchParams.append(key, value.toString());
	}
	return url.toString();
};

export const frameSiteExportUrl = (frame: Frame) => {
	if (!frame.siteId) return null;
	const url = new URL(VITE_FORGE_URL + `/site/${frame.siteId}/export`);
	url.searchParams.append('frameId', frame.id);

	// Add visual edit hash if active (so exported HTML includes visual edits)
	const veHash = getActiveVisualEditsHash(frame);
	if (veHash) {
		url.searchParams.append('veHash', veHash);
	}
	if (frame.metadata?.mode) {
		url.searchParams.append('mode', frame.metadata.mode);
	}

	return url.toString();
};

export const siteUrl = (siteId: string) => {
	const url = new URL(VITE_FORGE_URL + `/site/${siteId}`);
	return url.toString();
};

// URL the browser navigates to to download a stored export. Forge verifies auth, marks
// the ExportLink's downloadedAt, and redirects to a fresh signed GCS URL.
export const exportDownloadUrl = (exportId: string): string =>
	`${VITE_FORGE_URL}/api/export/download/${encodeURIComponent(exportId)}`;

export const frameShareUrl = (frame: Frame) => {
	const base = window.location.origin;
	const path = `/frame/${frame.id}/${urlSafeString(frame.title)}`;
	const url = new URL(`${base}${path}`);
	url.searchParams.append('shared', 'frame');
	return url.toString();
};

export const sceneShareUrl = (scene: Scene) => {
	const base = window.location.origin;
	const path = routes.scene(scene.id, scene.title);
	const url = new URL(`${base}${path}`);
	url.searchParams.append('shared', 'scene');
	return url.toString();
};

export const designSystemShareUrl = (designSystem: DesignSystem) => {
	const base = window.location.origin;
	const path = routes.designSystem(designSystem.id, designSystem.title);
	const url = new URL(`${base}${path}`);
	url.searchParams.append('shared', 'design-system');
	return url.toString();
};

export const urlSafeString = (s: string) => {
	return s
		.trim()
		.normalize('NFKD') // Normalize accented characters
		.replace(/[\u0300-\u036f]/g, '') // Remove diacritics
		.replace(/[^A-Za-z0-9]+/g, '-') // Replace non-alphanumerics with hyphens
		.replace(/-+/g, '-') // Collapse multiple hyphens
		.replace(/^-+|-+$/g, ''); // Trim leading/trailing hyphens
};

export const navigate = (path: string) => {
	window.history.pushState({}, '', path);
	window.dispatchEvent(new PopStateEvent('popstate'));
};

//------------------------------------------------------------------------------------------------
// TIME HELPERS
//------------------------------------------------------------------------------------------------

export function formatRelativeTime(date: Date): string {
	const now = new Date();
	const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

	if (diffInSeconds < 60) {
		return 'just now';
	}

	const diffInMinutes = Math.floor(diffInSeconds / 60);
	if (diffInMinutes < 60) {
		return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
	}

	const diffInHours = Math.floor(diffInMinutes / 60);
	if (diffInHours < 24) {
		return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
	}

	const diffInDays = Math.floor(diffInHours / 24);
	if (diffInDays < 7) {
		return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
	}

	const diffInWeeks = Math.floor(diffInDays / 7);
	if (diffInWeeks < 4) {
		return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
	}

	const diffInMonths = Math.floor(diffInDays / 30);
	if (diffInMonths < 12) {
		return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
	}

	const diffInYears = Math.floor(diffInDays / 365);
	return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
}

//------------------------------------------------------------------------------------------------
// FILE UPLOAD HELPERS
//------------------------------------------------------------------------------------------------

export interface UploadTracker {
	filename: string;
	contentType: string;
	size: number;
	uniqueId: string;
	previewUrl?: string; // local blob URL for image previews during upload
}

export type FileUploadCallbacks = {
	onLoadingStart: (uploadTracker: UploadTracker) => void;
	onLoadingEnd: (uniqueId: string) => void;
	onSuccess: (attachment: Attachment) => void;
	onError?: (error: Error) => void;
};

export function canUpload(file: File): { canUpload: boolean; error?: string } {
	const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
	if (file.size > MAX_FILE_SIZE) {
		const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
		return {
			canUpload: false,
			error: `File size must be less than ${maxSizeMB}MB`,
		};
	}
	return { canUpload: true };
}

// Tracks in-flight uploads so a pending attachment can be canceled. Without this a
// slow/hung upload can't be aborted and blocks the chat (submit stays disabled
// while an attachment is loading). Keyed by UploadTracker.uniqueId.
const uploadAbortControllers = new Map<string, AbortController>();

export function cancelUpload(uniqueId: string): void {
	const controller = uploadAbortControllers.get(uniqueId);
	if (!controller) return;
	uploadAbortControllers.delete(uniqueId);
	controller.abort();
}

// Aborted requests are user-initiated cancels, not failures worth reporting.
const isAbortError = (error: unknown): boolean => {
	const e = error as { code?: string; name?: string } | null;
	return e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
};

export async function uploadFile(file: File, callbacks: FileUploadCallbacks): Promise<void> {
	const validation = canUpload(file);
	if (!validation.canUpload) {
		callbacks.onError?.(new Error(validation.error || 'File size exceeds limit'));
		return;
	}

	const uniqueId = uuid();
	const uploadTracker: UploadTracker = {
		filename: file.name,
		contentType: file.type,
		size: file.size,
		uniqueId,
		previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
	};

	callbacks.onLoadingStart(uploadTracker);

	const controller = new AbortController();
	uploadAbortControllers.set(uniqueId, controller);

	// Settle: drop the loading tile and release the preview blob + controller.
	const finish = () => {
		callbacks.onLoadingEnd(uniqueId);
		uploadAbortControllers.delete(uniqueId);
		if (uploadTracker.previewUrl) URL.revokeObjectURL(uploadTracker.previewUrl);
	};

	try {
		// Read file as base64
		const reader = new FileReader();
		reader.onload = async () => {
			try {
				const base64Data = (reader.result as string).split(',')[1]; // Remove data URL prefix

				const fileResponse: api.CreateFileResponse = await forge.createFile(
					{ data: base64Data },
					controller.signal
				);

				finish();

				const attachment: Attachment = {
					filename: file.name,
					contentType: file.type,
					storagePath: fileResponse.storagePath,
					size: file.size,
				};

				callbacks.onSuccess(attachment);
			} catch (error) {
				finish();
				if (!isAbortError(error)) Sentry.captureException(error);
			}
		};

		reader.onerror = () => {
			finish();
		};

		reader.readAsDataURL(file);
	} catch (error) {
		finish();
		Sentry.captureException(error);
	}
}

// Handler for file dialog trigger
export const onUploadFileDialog = (callbacks: FileUploadCallbacks, multiple?: boolean): void => {
	const input = document.createElement('input');
	input.type = 'file';
	if (multiple) {
		input.multiple = true;
	}

	input.onchange = async (e) => {
		const files = (e.target as HTMLInputElement).files;
		if (!files) return;

		for (let i = 0; i < files.length; i++) {
			await uploadFile(files[i], callbacks);
		}
	};

	input.click();
};

// Handler for paste events
export const onFilePasted = (
	callbacks: FileUploadCallbacks
): ((e: React.ClipboardEvent) => Promise<void>) => {
	return async (e: React.ClipboardEvent) => {
		const items = e.clipboardData?.items;
		if (!items) return;

		let processed = false;
		for (const item of items) {
			if (item.kind === 'file') {
				const file = item.getAsFile();
				if (file) {
					await uploadFile(file, callbacks);
					processed = true;
				}
			}
		}
		if (processed) {
			e.preventDefault();
		}
	};
};

// Handler for drop events
export const onFileDropped = (
	callbacks: FileUploadCallbacks
): ((e: React.DragEvent) => Promise<void>) => {
	return async (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const files = Array.from(e.dataTransfer.files);
		if (!files.length) return;

		for (const file of files) {
			await uploadFile(file, callbacks);
		}
	};
};

export const PASTE_AS_ATTACHMENT_THRESHOLD = 2000;

// Checks whether text is a complete CSS stylesheet (not code or prose that
// happens to contain braces). Requires CSS rule structure and rejects content
// with markup, JS keywords, or markdown patterns.
function looksLikePureCss(text: string): boolean {
	if (!text.includes('{') || !text.includes('}')) return false;
	if (text.includes('<') || text.includes('>')) return false;
	if (
		/^(import |export |const |let |var |function |return |if \(|else |for \(|while \()/m.test(text)
	)
		return false;
	if (/^#{1,6}\s/m.test(text)) return false;
	const hasCssConstructs =
		/@(media|keyframes|import|font-face|charset|layer)\b/.test(text) ||
		/^:root\s*\{/m.test(text) ||
		/^[.#:][a-z][\w-]*[\s,{]/im.test(text);
	return hasCssConstructs;
}

// Detects file type from pasted text. The entire document must parse as the
// detected format — a document that merely *contains* SVG or CSS patterns
// inside other content falls through to plain text.
function detectPastedFileType(text: string): { filename: string; contentType: string } {
	const trimmed = text.trim();
	const lower = trimmed.toLowerCase();

	// SVG: parse as XML, root element must be <svg>
	if (lower.startsWith('<svg') || lower.startsWith('<?xml')) {
		try {
			const doc = new DOMParser().parseFromString(trimmed, 'text/xml');
			if (
				!doc.querySelector('parsererror') &&
				doc.documentElement.tagName.toLowerCase() === 'svg'
			) {
				return { filename: 'pasted.svg', contentType: 'image/svg+xml' };
			}
		} catch {
			// not valid SVG
		}
	}

	// HTML: must start with doctype or <html> and end with </html> (allow trailing whitespace)
	if (
		(lower.startsWith('<!doctype html') || lower.startsWith('<html')) &&
		/<\/html>\s*$/.test(lower)
	) {
		return { filename: 'pasted.html', contentType: 'text/html' };
	}

	// JSON: must fully parse
	if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
		try {
			JSON.parse(trimmed);
			return { filename: 'pasted.json', contentType: 'application/json' };
		} catch {
			// not valid JSON
		}
	}

	// CSS: entire document must look like a stylesheet
	if (looksLikePureCss(trimmed)) {
		return { filename: 'pasted.css', contentType: 'text/css' };
	}

	return { filename: 'pasted.txt', contentType: 'text/plain' };
}

// Deduplicates a filename against existing names.
// "pasted.svg" -> "pasted-2.svg" -> "pasted-3.svg" etc.
function deduplicateFilename(base: string, existingNames: string[]): string {
	const names = new Set(existingNames);
	if (!names.has(base)) return base;
	const dot = base.lastIndexOf('.');
	const stem = dot === -1 ? base : base.slice(0, dot);
	const ext = dot === -1 ? '' : base.slice(dot);
	let counter = 2;
	while (names.has(`${stem}-${counter}${ext}`)) counter++;
	return `${stem}-${counter}${ext}`;
}

// Checks if pasted text exceeds the threshold and, if so, uploads it as a
// typed file attachment. Returns true if the paste was intercepted.
export function maybeUploadPastedText(
	text: string,
	existingFilenames: string[],
	callbacks: FileUploadCallbacks
): boolean {
	if (text.length < PASTE_AS_ATTACHMENT_THRESHOLD) return false;
	const { filename: base, contentType } = detectPastedFileType(text);
	const filename = deduplicateFilename(base, existingFilenames);
	const blob = new Blob([text], { type: contentType });
	const file = new File([blob], filename, { type: contentType });
	uploadFile(file, callbacks);
	return true;
}

export const mixpanelTrackEvent = (eventName: string, eventProperties: Record<string, any>) => {
	try {
		if (typeof window.mixpanel !== 'undefined') {
			window.mixpanel.track(eventName, eventProperties);
		}
	} catch (e) {}
};

export const mixpanelIdentify = (traits: Record<string, any>) => {
	try {
		if (typeof window.mixpanel !== 'undefined') {
			window.mixpanel.people.union(traits);
		}
	} catch (e) {}
};

// opens a popup for GitHub OAuth, resolves true on success, false if user closed without completing
export const openGithubAuthPopup = (): Promise<boolean> => {
	return new Promise((resolve) => {
		const w = 600;
		const h = 700;
		const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
		const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
		const popup = window.open(
			`${VITE_FORGE_URL}/auth/github`,
			'github-auth',
			`width=${w},height=${h},left=${left},top=${top},popup=yes`
		);

		if (!popup) {
			resolve(false);
			return;
		}

		const expectedOrigin = new URL(VITE_FORGE_URL).origin;
		const onMessage = (event: MessageEvent) => {
			if (event.origin === expectedOrigin && event.data?.type === 'github-auth-complete') {
				cleanup();
				resolve(true);
			}
		};

		const pollClosed = setInterval(() => {
			if (popup.closed) {
				cleanup();
				resolve(false);
			}
		}, 500);

		const cleanup = () => {
			window.removeEventListener('message', onMessage);
			clearInterval(pollClosed);
		};

		window.addEventListener('message', onMessage);
	});
};

// opens a popup for Figma OAuth, resolves true on success, false if user closed without completing
export const openFigmaAuthPopup = (): Promise<boolean> => {
	return new Promise((resolve) => {
		const startedAt = Date.now();
		const w = 600;
		const h = 700;
		const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
		const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
		const popup = window.open(
			`${VITE_FORGE_URL}/auth/figma`,
			'figma-auth',
			`width=${w},height=${h},left=${left},top=${top},popup=yes`
		);

		if (!popup) {
			resolve(false);
			return;
		}

		const expectedOrigin = new URL(VITE_FORGE_URL).origin;
		const onMessage = (event: MessageEvent) => {
			if (event.origin === expectedOrigin && event.data?.type === 'figma-auth-complete') {
				cleanup();
				resolve(true);
			}
		};

		const pollClosed = setInterval(() => {
			if (popup.closed) {
				const elapsedMs = Date.now() - startedAt;
				// likely-stuck flows show up as long-open popups closed without a success message;
				// quick closes are usually deliberate cancels
				if (elapsedMs >= 8000) {
					Sentry.captureMessage('Figma OAuth: popup closed without success message', {
						level: 'warning',
						extra: { elapsedMs, ua: navigator.userAgent },
					});
				}
				cleanup();
				resolve(false);
			}
		}, 500);

		const cleanup = () => {
			window.removeEventListener('message', onMessage);
			clearInterval(pollClosed);
		};

		window.addEventListener('message', onMessage);
	});
};
